/* LumaSpec product page (PRODUCT lane): stock line + quote item follow Horizon's variant picker, Add to quote,
   and the 3D model view (model-viewer, self-hosted as ls-product-3d.js, loaded on the first click only).
   Quote list: window.LSQuote from ls-base.js (SHELL); a local fallback keeps the same storage key and item shape. */

const QKEY = 'lumaspec-quote-v1';

/* Interface strings: snippets/ls-product-i18n.liquid (locales ls.product; placeholders {n} in single braces so
   Liquid's t filter leaves them for this script), English defaults otherwise. */
const STR = {
  added_one: 'Added to your quote list ({n} item)',
  added_other: 'Added to your quote list ({n} items)',
  view_list: 'View list',
  model_alt: '3D model: drag to rotate, scroll to zoom',
};
try {
  Object.assign(STR, JSON.parse(document.querySelector('[data-ls-product-i18n]')?.textContent || '{}'));
} catch {
  /* keep the defaults */
}

/* the t filter HTML-escapes plain (non-_html) keys: back to plain text once (shown via textContent / esc()) */
for (const k of Object.keys(STR)) {
  if (typeof STR[k] === 'string' && STR[k].includes('&')) {
    const d = document.createElement('textarea');
    d.innerHTML = STR[k];
    STR[k] = d.value;
  }
}
/** @param {string} key @param {Record<string, string|number>} [vars] */
const tr = (key, vars = {}) => String(STR[key] ?? key).replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? ''));

function quoteAdd(item) {
  if (window.LSQuote && typeof window.LSQuote.add === 'function') return window.LSQuote.add(item);
  let list = [];
  try {
    list = JSON.parse(localStorage.getItem(QKEY) || '[]') || [];
  } catch {
    list = [];
  }
  const key = (x) => `${x.h}|${x.sku}|${JSON.stringify(x.o || null)}`;
  const hit = list.find((x) => key(x) === key(item));
  if (hit) hit.q += item.q;
  else list.push(item);
  try {
    localStorage.setItem(QKEY, JSON.stringify(list));
  } catch {
    /* storage blocked: the list lasts for this page */
  }
  for (const el of document.querySelectorAll('[data-quote-count]')) el.textContent = String(list.length);
  return list;
}

function readJSON(el) {
  if (!el) return null;
  try {
    return JSON.parse(el.textContent || 'null');
  } catch {
    return null;
  }
}

function buildLinks(pdp) {
  const item = readJSON(pdp.querySelector('[data-ls-quote-item]'));
  if (!item?.h) return [];
  const options = pdp.querySelector('[data-ls-rfq-dialog] [data-rfq-opts]');
  if (options?.parentElement && item.sku) {
    const label = options.parentElement;
    if (label.firstChild?.nodeType === Node.TEXT_NODE) label.firstChild.textContent = item.sku + ' ';
    else label.insertBefore(document.createTextNode(item.sku + ' '), options);
  }
  const quantity = pdp.querySelector('[data-ls-qty],input[name="quantity"]');
  const n = Math.max(1, parseInt(quantity?.value || '1', 10) || 1);
  const tracking = new URLSearchParams(location.search);
  const links = [];
  for (const link of document.querySelectorAll('a[href*="/pages/configure?"]')) {
    const target = new URL(link.href, location.href);
    if (target.origin !== location.origin) continue;
    const step = ['camera', 'lens', 'light', 'controller', 'pc', 'trigger', 'extras', 'stand']
      .find((key) => target.searchParams.get(key) === item.h.replace(/^lumaspec-/, ''));
    if (!step) continue;
    target.searchParams.set('selection', String(item.v || item.sku || ''));
    target.searchParams.set('quantity', String(n));
    target.searchParams.set('start', step);
    tracking.forEach((value, key) => {
      if (/^(utm_[a-z_]+|gclid|dclid|gbraid|wbraid|msclkid|fbclid)$/.test(key)) target.searchParams.set(key, value);
    });
    link.href = target.href;
    link.dataset.lsProductBuild = pdp.dataset.productId;
    link.setAttribute('aria-disabled', String(pdp.dataset.lsSelectionPending === '1'));
    links.push(link);
  }
  return links;
}

function initPdp(pdp) {
  if (pdp.dataset.lsReady) return;
  pdp.dataset.lsReady = '1';
  const section = pdp.closest('.shopify-section') || document;
  const ac = new AbortController();
  const { signal } = ac;
  let selectionRevision = 0;
  const selectionBusy = (busy) => {
    pdp.dataset.lsSelectionPending = busy ? '1' : '0';
    pdp.querySelectorAll('[data-ls-quote-add],[data-ls-rfq-open]').forEach((button) => {
      if (busy) {
        if (!button.hasAttribute('data-ls-was-disabled')) button.dataset.lsWasDisabled = button.disabled ? '1' : '0';
        button.disabled = true;
      } else if (button.hasAttribute('data-ls-was-disabled')) {
        button.disabled = button.dataset.lsWasDisabled === '1';
        delete button.dataset.lsWasDisabled;
      }
    });
    buildLinks(pdp);
  };
  buildLinks(pdp);
  pdp.addEventListener('input', () => buildLinks(pdp), { signal });
  const quoteData = pdp.querySelector('[data-ls-quote-item]');
  const choiceObserver = new MutationObserver(() => buildLinks(pdp));
  if (quoteData) choiceObserver.observe(quoteData, { childList: true, subtree: true, characterData: true });
  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[data-ls-product-build]') : null;
    if (!link || link.dataset.lsProductBuild !== pdp.dataset.productId) return;
    if (pdp.dataset.lsSelectionPending === '1') { event.preventDefault(); return; }
    buildLinks(pdp); // includes a quantity changed immediately before clicking
  }, { signal, capture: true });

  // a new option: Horizon fetched the page for that variant; take our stock line and quote item from it
  section.addEventListener(
    'shopify:product:select',
    async (event) => {
      const promise = /** @type {any} */ (event).promise;
      if (!promise) return;
      if (event.target instanceof Element && event.target.closest('[data-ls-pdp]') !== pdp) return;
      const revision = ++selectionRevision;
      selectionBusy(true);
      try {
        const result = await promise;
        if (revision !== selectionRevision || !pdp.isConnected) return;
        const html = result && result.detail && result.detail.html;
        if (!html || typeof html.querySelector !== 'function' || !result.detail.resource || String(result.detail.productId) !== pdp.dataset.productId) return;
        for (const sel of ['[data-ls-stock]', '[data-ls-quote-item]', '[data-ls-model]', '[data-ls-rfq-dialog] .ls-rfqd__prod']) {
          const now = pdp.querySelector(sel);
          const next = html.querySelector(`[data-ls-pdp] ${sel}`);
          if (now && next) now.innerHTML = next.innerHTML;
        }
        // QA26b: the spec table's Model row (another section) names the same order number as the buy box
        const sku = pdp.querySelector('[data-ls-model]');
        if (sku) for (const el of document.querySelectorAll('[data-ls-spec-model]')) el.textContent = sku.textContent || '';
        selectionBusy(false);
      } catch {
        /* aborted by a newer selection or a failed fetch: keep the current line */
      }
    },
    { signal }
  );

  // Add to quote: quantity from our field (quote modes) or Horizon's quantity selector (buy mode)
  pdp.addEventListener(
    'click',
    (event) => {
      const btn = event.target instanceof Element ? event.target.closest('[data-ls-quote-add]') : null;
      if (!btn) return;
      if (pdp.dataset.lsSelectionPending === '1') return;
      const item = readJSON(pdp.querySelector('[data-ls-quote-item]'));
      if (!item) return;
      const qEl = pdp.querySelector('[data-ls-qty]') || pdp.querySelector('input[name="quantity"]');
      const q = Math.max(1, parseInt(qEl && 'value' in qEl ? String(qEl.value) : '1', 10) || 1);
      const url = item.url || '/pages/quote';
      const list = quoteAdd({ v: String(item.v || ''), sku: item.sku || '', t: item.t, h: item.h, i: item.i || '', q, o: item.o || undefined });
      const toast = pdp.querySelector('[data-ls-toast]');
      if (toast) {
        toast.hidden = false;
        toast.innerHTML = '';
        toast.append(`${tr(list.length === 1 ? 'added_one' : 'added_other', { n: list.length })} · `);
        const a = document.createElement('a');
        a.href = url;
        a.textContent = tr('view_list');
        toast.append(a);
        clearTimeout(Number(toast.dataset.t));
        toast.dataset.t = String(setTimeout(() => (toast.hidden = true), 6000));
      }
    },
    { signal }
  );

  // 3D: the GLB opens over the gallery in model-viewer; the library loads on the first click
  const open = pdp.querySelector('[data-ls-3d-open]');
  const box = pdp.querySelector('[data-ls-3d]');
  if (open && box instanceof HTMLElement) {
    open.addEventListener(
      'click',
      () => {
        const lib = box.dataset.lib;
        if (lib && !document.querySelector(`script[src="${lib}"]`)) {
          const s = document.createElement('script');
          s.type = 'module';
          s.src = lib;
          document.head.appendChild(s);
        }
        let mv = box.querySelector('model-viewer');
        if (!mv) {
          mv = document.createElement('model-viewer');
          const attrs = {
            'camera-controls': '',
            'auto-rotate': '',
            'shadow-intensity': '1',
            'environment-image': 'neutral',
            exposure: '1.2',
            'camera-orbit': '30deg 62deg auto',
            'interaction-prompt': 'none',
            alt: tr('model_alt'),
            src: box.dataset.src || '',
          };
          for (const [k, v] of Object.entries(attrs)) mv.setAttribute(k, v);
          box.appendChild(mv);
        }
        box.hidden = false;
        open.setAttribute('aria-pressed', 'true');
        box.querySelector('[data-ls-3d-close]')?.focus();
      },
      { signal }
    );
    box.querySelector('[data-ls-3d-close]')?.addEventListener(
      'click',
      () => {
        box.hidden = true;
        open.setAttribute('aria-pressed', 'false');
        open.focus();
      },
      { signal }
    );
    box.addEventListener('keydown', (e) => e.key === 'Escape' && box.querySelector('[data-ls-3d-close]')?.click(), { signal });
  }

  // a section re-render (theme editor) drops this element: release the listeners
  new MutationObserver((_, obs) => {
    if (!pdp.isConnected) {
      ac.abort();
      choiceObserver.disconnect();
      obs.disconnect();
    }
  }).observe(section === document ? document.body : section, { childList: true });
}

// related products: fetch the cards (sections/ls-product-related-cards.liquid, lsv.related) when the section nears the
// viewport. QA18c (2026-10-27): the list is chosen from the catalog (src/catalog/related.py: same brand family,
// category and key interface, nearest main size, never this series); the rel13 / QA15 browser filters on Shopify's
// "related" recommendations (relKey / relFits) and the "More <category>" fallback are gone.
function initRelated(el) {
  if (el.dataset.lsReady || !el.dataset.url) return;
  el.dataset.lsReady = '1';
  // not this product, not a product already linked on the page (series table, fits, versus), at most 2 of any other
  // series, at most data-show cards; fewer than 2 -> the section stays empty (hidden, no heading). QA22c: models of
  // this product's own series come only when lsv.related chose them (src/catalog/related.py: no other series matches
  // and the series is bigger than its table); the ones the series table already lists drop as on-page links
  const onPage = new Set();
  const pick = async (url) => {
    const res = await fetch(url);
    if (!res.ok) return null;
    const text = await res.text();
    if (text.includes('Liquid error')) return null; // a missing snippet (e.g. the card not in this theme yet): show nothing
    const next = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-ls-related]');
    if (!next) return null;
    const show = parseInt(el.dataset.show || '8', 10) || 8;
    let kept = 0;
    const per = {};
    next.querySelectorAll('.ls-pdr__item').forEach((it) => {
      const h = it.getAttribute('data-handle') || '';
      const s = it.getAttribute('data-series') || '';
      const drop = h === el.dataset.handle || onPage.has(h) || (s && s !== el.dataset.series && (per[s] || 0) >= 2) || kept >= show;
      if (drop) it.remove();
      else {
        kept++;
        if (s) per[s] = (per[s] || 0) + 1;
      }
    });
    const h2 = next.querySelector('.ls-pdr__head .ls-h2');
    if (h2 && el.dataset.heading) h2.textContent = el.dataset.heading; // the section's heading setting
    return kept >= 2 ? next.innerHTML : null;
  };
  const load = async () => {
    if (el.dataset.lsLoading) return;
    el.dataset.lsLoading = '1';
    try {
      document.querySelectorAll('a[href*="/products/"]').forEach((a) => {
        if (el.contains(a)) return;
        const m = (a.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
        if (m) onPage.add(decodeURIComponent(m[1]));
      });
      const html = await pick(el.dataset.url);
      if (html) el.innerHTML = html;
    } catch {
      /* no related products: the section stays empty */
    }
  };
  // perf16 (2026-10-27): armed only once the page has loaded. Sections below hold a 640 px placeholder until painted,
  // so a 3000 px margin was met at once and the 1-3 fetches + parse ran during the page load (~0.7-1.4 s on a phone)
  if (document.readyState !== 'complete') {
    window.addEventListener('load', () => setTimeout(() => arm(el, load), 0), { once: true });
    return;
  }
  arm(el, load);
}

function arm(el, load) {
  if (!('IntersectionObserver' in window)) return void load();
  const io = new IntersectionObserver(
    (entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        io.disconnect();
        load();
      }
    },
    // QA14 (CLS): far ahead, so the cards are in before the reader gets near (at 600 px they arrived with the footer
    // on screen and pushed it away: layout shift 0.24-0.86 on phones)
    { rootMargin: '3000px 0px' }
  );
  // rel13: the empty shell is display:none (.ls-pdr:empty) and a box-less element never intersects: watch the section
  io.observe(el.closest('.shopify-section') || el.parentElement || el);
  // QA14: and in any case once the page is idle (a fast reader or a jump link reached the footer before the cards)
  const idle = () => {
    io.disconnect();
    load();
  };
  if ('requestIdleCallback' in window) requestIdleCallback(idle, { timeout: 2500 });
  else setTimeout(idle, 1500);
}

const run = () => {
  document.querySelectorAll('[data-ls-pdp]').forEach((el) => initPdp(/** @type {HTMLElement} */ (el)));
  document.querySelectorAll('[data-ls-related]').forEach((el) => initRelated(/** @type {HTMLElement} */ (el)));
};
run();
document.addEventListener('shopify:section:load', run);
